import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';

import { useAuthContext } from '../../context/auth-context';
import { useGetObsSyncHealthQuery } from '../../redux/services/obs-api';
import { ObsError } from './ObsError';

const STATUS = {
  ok: { label: 'Al día', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  warn: { label: 'Pendientes', color: 'warning', icon: <WarningAmberIcon fontSize="small" /> },
  red: { label: 'Sin sincronizar', color: 'error', icon: <ErrorIcon fontSize="small" /> },
};

const fmtDate = value => (value ? new Date(value).toLocaleString() : '—');

function SyncHealthTab() {
  const { activeCamp } = useAuthContext();
  const { data, isLoading, isFetching, error } = useGetObsSyncHealthQuery(activeCamp?._id, {
    skip: !activeCamp?._id,
    pollingInterval: 30000,
  });

  if (!activeCamp?._id) {
    return <Typography sx={{ p: 2 }}>Selecciona un campamento para ver la salud de sincronización.</Typography>;
  }
  if (isLoading) return <CircularProgress sx={{ m: 4 }} />;
  if (error) return <ObsError error={error} context="la salud de sincronización" />;

  const rows = data?.results || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} jueces con rúbricas asignadas · actualizado {fmtDate(data?.generatedAt)}
          {isFetching ? ' · refrescando…' : ''}
        </Typography>
      </Box>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Estado</TableCell>
              <TableCell>Juez</TableCell>
              <TableCell align="right">Rúbricas</TableCell>
              <TableCell align="right">Pendientes</TableCell>
              <TableCell align="right">Calificadas</TableCell>
              <TableCell>Última sync</TableCell>
              <TableCell>Último login móvil</TableCell>
              <TableCell>App</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const status = STATUS[row.status] || STATUS.red;
              const platform = row.lastSync?.platform || row.lastLogin?.platform;
              const appVersion = row.lastSync?.appVersion || row.lastLogin?.appVersion;
              return (
                <TableRow key={row.userId} hover>
                  <TableCell>
                    <Chip size="small" color={status.color} icon={status.icon} label={status.label} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220, overflow: 'hidden' }}>
                    <Typography variant="body2" noWrap title={row.fullname || row.username || row.userId}>
                      {row.fullname || row.username || row.userId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {row.username}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.assignedRubrics}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: row.pendingCells > 0 ? 700 : 400 }}
                      color={row.pendingCells > 0 ? 'error' : 'text.primary'}
                    >
                      {row.pendingCells}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{`${row.gradedCells}/${row.totalCells}`}</TableCell>
                  <TableCell>
                    {row.lastSync ? (
                      <Tooltip
                        title={`batch ${row.lastSync.batchSize ?? '—'} · no encontradas ${row.lastSync.notFoundCount ?? '—'} · install ${row.lastSync.installId || '—'}`}
                      >
                        <Box>
                          <Typography variant="body2">{fmtDate(row.lastSync.at)}</Typography>
                          {!row.lastSync.ok && (
                            <Chip size="small" color="error" label="falló" sx={{ mt: 0.25 }} />
                          )}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        nunca vista
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.lastLogin ? fmtDate(row.lastLogin.at) : '—'}</TableCell>
                  <TableCell>
                    {platform ? `${platform}${appVersion ? ` ${appVersion}` : ''}` : '—'}
                  </TableCell>
                  <TableCell>{row.lastSync?.ip || row.lastLogin?.ip || '—'}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography sx={{ p: 2 }} color="text.secondary">
                    No hay jueces con rúbricas asignadas en este campamento.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default SyncHealthTab;
